import { onlineManager } from '@tanstack/react-query';
import { Network } from '@capacitor/network';
import { isCapacitorNative } from '@/utils/platformUtils';

/**
 * Drives TanStack Query's `onlineManager` from *internet* reachability, not
 * merely "a network interface is up".
 *
 * Why this matters for offline POS: when `onlineManager` reports offline, Query
 * *pauses* mutations (queues them) instead of failing them, and skips refetches.
 * On reconnect it resumes paused mutations and refetches.
 *
 * `navigator.onLine` and `@capacitor/network` only know about the local link
 * (Wi-Fi associated, Ethernet plugged in). A tablet on a LAN with no WAN —
 * a common restaurant setup — still reports `connected: true`, so mutations
 * would hit Supabase, fail, and roll back instead of queueing. We treat a down
 * link as definitely offline, and when the link is up we probe the registry
 * (the backend we actually need) before calling the device online.
 *
 * Going offline is reactive: a real write fails (`notifyNetworkFailure`) or the
 * radio drops. The 10s probe runs only while already offline so we can resume
 * the queue when WAN returns without a radio flap (no `online` event).
 *
 * Call once during app bootstrap. Returns a cleanup function.
 */

export const INTERNET_PROBE_TIMEOUT_MS = 4_000;
export const INTERNET_PROBE_INTERVAL_MS = 10_000;

const FALLBACK_PROBE_URL = 'https://connectivitycheck.gstatic.com/generate_204';

const getProbeUrl = (): string => {
  const registryUrl = import.meta.env.VITE_REGISTRY_SUPABASE_URL as string | undefined;
  if (registryUrl) {
    const base = registryUrl.replace(/\/$/, '');
    return `${base}/auth/v1/health?cb=${Date.now()}`;
  }
  return `${FALLBACK_PROBE_URL}?cb=${Date.now()}`;
};

let inFlightProbe: Promise<boolean> | null = null;
let probeGeneration = 0;

/**
 * True when a request to the registry (or a public connectivity URL) completes.
 * `no-cors` so a CORS miss cannot look like "still offline" and block recovery.
 * HTTP status is ignored (opaque 401 still means the network path works).
 * Times out so a black-holed Wi-Fi gateway cannot hang POS writes.
 */
export const probeInternetReachability = (): Promise<boolean> => {
  if (inFlightProbe) return inFlightProbe;

  inFlightProbe = (async () => {
    if (typeof fetch !== 'function') return true;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), INTERNET_PROBE_TIMEOUT_MS);
    try {
      await fetch(getProbeUrl(), {
        method: 'GET',
        cache: 'no-store',
        mode: 'no-cors',
        signal: controller.signal,
      });
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  })().finally(() => {
    inFlightProbe = null;
  });

  return inFlightProbe;
};

const getLinkIsUp = async (): Promise<boolean> => {
  if (isCapacitorNative()) {
    try {
      const status = await Network.getStatus();
      return status.connected;
    } catch {
      // Plugin unavailable: fall through to browser signals.
    }
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }
  return true;
};

export const initOnlineManager = (): (() => void) => {
  onlineManager.setEventListener((setOnline) => {
    let cancelled = false;
    let linkIsUp = true;
    let removeNativeListener: (() => void) | undefined;

    const applyOffline = () => {
      probeGeneration += 1;
      setOnline(false);
    };

    const confirmInternet = async () => {
      if (!linkIsUp) {
        applyOffline();
        return;
      }
      const generation = ++probeGeneration;
      const reachable = await probeInternetReachability();
      if (cancelled || generation !== probeGeneration) return;
      setOnline(reachable);
    };

    const applyLinkStatus = (connected: boolean) => {
      linkIsUp = connected;
      if (!connected) {
        applyOffline();
        return;
      }
      void confirmInternet();
    };

    const updateFromBrowser = () => applyLinkStatus(navigator.onLine);

    if (isCapacitorNative()) {
      Network.getStatus()
        .then((status) => {
          if (cancelled) return;
          applyLinkStatus(status.connected);
        })
        .catch(() => {
          if (cancelled) return;
          applyLinkStatus(true);
        });

      Network.addListener('networkStatusChange', (status) => {
        applyLinkStatus(status.connected);
      })
        .then((handle) => {
          removeNativeListener = () => {
            void handle.remove();
          };
        })
        .catch(() => undefined);
    } else {
      updateFromBrowser();
      window.addEventListener('online', updateFromBrowser);
      window.addEventListener('offline', updateFromBrowser);
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && linkIsUp) {
        void confirmInternet();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const intervalId = setInterval(() => {
      // Only needed to recover: WAN can return while Wi-Fi never dropped, so
      // there is no `online` event. Do not poll while already online.
      if (linkIsUp && !onlineManager.isOnline()) void confirmInternet();
    }, INTERNET_PROBE_INTERVAL_MS);

    return () => {
      cancelled = true;
      probeGeneration += 1;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      removeNativeListener?.();
      window.removeEventListener('online', updateFromBrowser);
      window.removeEventListener('offline', updateFromBrowser);
    };
  });

  return () => {
    // Resetting clears our custom listener; Query falls back to its default.
    onlineManager.setEventListener(() => () => undefined);
  };
};

/** Current connectivity as understood by TanStack Query. */
export const getIsOnline = (): boolean => onlineManager.isOnline();

/**
 * Flip Query to offline so in-flight mutation retries pause and queue instead
 * of rolling back optimistic POS writes. Used when a request already failed
 * with a network error. The offline-only probe resumes Query when WAN returns.
 */
export const notifyNetworkFailure = (): void => {
  probeGeneration += 1;
  onlineManager.setOnline(false);
};

/**
 * Device-level connectivity check that works before `initOnlineManager` has
 * run (e.g. during auth bootstrap, which fires before React effects).
 * `onlineManager` defaults to "online" until its listener is installed, so it
 * can't be trusted alone that early. A down link is enough to call it offline;
 * a up link still needs a reachability probe — Wi-Fi without WAN is offline
 * for POS purposes.
 */
export const isDeviceOffline = async (): Promise<boolean> => {
  if (!(await getLinkIsUp())) return true;
  return !(await probeInternetReachability());
};
